import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Clock, XCircle, ExternalLink, RefreshCw, Copy, Truck, PackageCheck } from "lucide-react";
import { api, formatINR } from "../lib/api";
import { toast } from "sonner";

const STATUS_TABS = [
  { key: "all", label: "All" },
  { key: "pending_payment", label: "Awaiting Payment" },
  { key: "confirmed", label: "Confirmed / Paid" },
  { key: "shipped", label: "Shipped" },
  { key: "delivered", label: "Delivered" },
  { key: "expired", label: "Expired" },
];

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("pending_payment");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { status: filter };
      const r = await api.get("/admin/orders", { params });
      setOrders(r.data);
    } catch (e) {
      toast.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filter]);

  const markPaid = async (order) => {
    if (!window.confirm(`Mark order ${order.order_number} as PAID?`)) return;
    try {
      await api.patch(`/admin/orders/${order.order_number}/mark-paid`);
      toast.success(`Order ${order.order_number} marked paid`);
      load();
    } catch (e) {
      toast.error("Could not mark paid");
    }
  };

  const markShipped = async (order) => {
    if (!window.confirm(`Mark order ${order.order_number} as SHIPPED?`)) return;
    try {
      await api.patch(`/admin/orders/${order.order_number}/mark-shipped`);
      toast.success(`Order ${order.order_number} marked shipped`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not mark shipped");
    }
  };

  const markDelivered = async (order) => {
    if (!window.confirm(`Mark order ${order.order_number} as DELIVERED?`)) return;
    try {
      await api.patch(`/admin/orders/${order.order_number}/mark-delivered`);
      toast.success(`Order ${order.order_number} marked delivered`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not mark delivered");
    }
  };

  const copyWhatsAppMessage = (order) => {
    const paymentLink = order.payment_link_used || "";
    const items = order.items.map((i) => `• ${i.name} × ${i.quantity} — ${formatINR(i.price * i.quantity)}`).join("\n");
    const msg = `Hi ${order.customer_name.split(" ")[0]}, thank you for your MileForge order #${order.order_number}!\n\n${items}\nTotal: ${formatINR(order.total)}\n\nComplete payment here: ${paymentLink}\n\nWe'll ship within 1-2 business days after payment.\n— MileForge Team`;
    navigator.clipboard.writeText(msg);
    toast.success("WhatsApp message copied");
  };

  const openWhatsApp = (order) => {
    const paymentLink = order.payment_link_used || "";
    const items = order.items.map((i) => `• ${i.name} × ${i.quantity} — ${formatINR(i.price * i.quantity)}`).join("\n");
    const text = encodeURIComponent(`Hi ${order.customer_name.split(" ")[0]}, thank you for your MileForge order #${order.order_number}!\n\n${items}\nTotal: ${formatINR(order.total)}\n\nComplete payment here: ${paymentLink}`);
    const phone = (order.phone || "").replace(/\D/g, "").slice(-10);
    window.open(`https://wa.me/91${phone}?text=${text}`, "_blank", "noopener,noreferrer");
  };

  const statusPill = (o) => {
    if (o.status === "expired") return <span className="inline-flex items-center gap-1 text-red-400 text-[10px] font-display tracking-widest uppercase"><XCircle size={12} /> Expired</span>;
    if (o.status === "delivered") return <span className="inline-flex items-center gap-1 text-emerald-400 text-[10px] font-display tracking-widest uppercase"><PackageCheck size={12} /> Delivered</span>;
    if (o.status === "shipped") return <span className="inline-flex items-center gap-1 text-sky-400 text-[10px] font-display tracking-widest uppercase"><Truck size={12} /> Shipped</span>;
    if (o.status === "confirmed" || o.payment_status === "PAID") return <span className="inline-flex items-center gap-1 text-emerald-400 text-[10px] font-display tracking-widest uppercase"><CheckCircle2 size={12} /> Paid</span>;
    return <span className="inline-flex items-center gap-1 text-yellow-400 text-[10px] font-display tracking-widest uppercase"><Clock size={12} className="animate-pulse" /> Awaiting Payment</span>;
  };

  return (
    <div data-testid="admin-orders-page" className="min-h-screen max-w-7xl mx-auto px-6 lg:px-10 py-12">
      <Link to="/admin" className="inline-flex items-center gap-2 text-xs text-white/60 hover:text-white mb-6"><ArrowLeft size={14} /> Back to Products</Link>
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="overline">Orders</div>
          <h1 className="section-title text-3xl sm:text-4xl">Order Management</h1>
        </div>
        <button onClick={load} className="btn-ghost" data-testid="admin-orders-refresh"><RefreshCw size={14} /> Refresh</button>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {STATUS_TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`admin-orders-tab-${t.key}`}
            onClick={() => setFilter(t.key)}
            className={`px-4 py-2 text-xs font-display tracking-widest uppercase border ${filter === t.key ? "bg-[#D96A1B] text-white border-[#D96A1B]" : "border-white/15 text-white/70 hover:border-white/40"}`}
          >{t.label}</button>
        ))}
      </div>

      {loading && <div className="text-white/50 text-sm">Loading…</div>}
      {!loading && orders.length === 0 && (
        <div className="text-center py-20 text-white/50 font-body" data-testid="admin-orders-empty">No orders in this bucket yet.</div>
      )}

      <div className="space-y-4">
        {orders.map((o) => (
          <div key={o.order_number} data-testid={`admin-order-${o.order_number}`} className="border border-white/10 p-5 hover:border-[#D96A1B]/40 transition-colors">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
              <div>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-display text-lg">#{o.order_number}</span>
                  {statusPill(o)}
                  <span className="text-[10px] text-white/40 font-body">{new Date(o.created_at).toLocaleString()}</span>
                </div>
                <div className="mt-1.5 text-sm font-body text-white/80">
                  <strong>{o.customer_name}</strong> · {o.phone} · {o.email}
                </div>
                <div className="text-xs text-white/50 mt-0.5">
                  {o.address}, {o.city}, {o.state} – {o.pincode}
                </div>
              </div>
              <div className="text-right">
                <div className="font-display text-2xl text-[#D96A1B]">{formatINR(o.total)}</div>
                <div className="text-[10px] text-white/40 uppercase tracking-widest">{o.items.length} item{o.items.length > 1 ? "s" : ""}</div>
              </div>
            </div>

            <div className="mt-4 grid gap-1.5 border-t border-white/5 pt-3">
              {o.items.map((i) => (
                <div key={i.product_id} className="flex items-center gap-3 text-sm text-white/70 font-body">
                  <img src={i.image} alt="" className="w-8 h-10 object-cover" />
                  <span className="flex-1 truncate">{i.name} × {i.quantity}</span>
                  <span>{formatINR(i.price * i.quantity)}</span>
                </div>
              ))}
            </div>

            {o.notes && <div className="mt-3 text-xs text-white/60 font-body italic border-l-2 border-[#D96A1B] pl-3">"{o.notes}"</div>}

            {o.status === "pending_payment" && (
              <div className="mt-4 flex flex-wrap gap-2 pt-3 border-t border-white/5">
                {o.payment_link_used && (
                  <a href={o.payment_link_used} target="_blank" rel="noreferrer" className="btn-ghost text-xs" data-testid={`admin-order-payment-link-${o.order_number}`}>
                    <ExternalLink size={12} /> View Payment Link
                  </a>
                )}
                <button onClick={() => openWhatsApp(o)} className="btn-ghost text-xs" data-testid={`admin-order-whatsapp-${o.order_number}`}>
                  💬 WhatsApp Customer
                </button>
                <button onClick={() => copyWhatsAppMessage(o)} className="btn-ghost text-xs" data-testid={`admin-order-copy-${o.order_number}`}>
                  <Copy size={12} /> Copy Message
                </button>
                <button onClick={() => markPaid(o)} className="btn-orange text-xs ml-auto" data-testid={`admin-order-mark-paid-${o.order_number}`}>
                  <CheckCircle2 size={14} /> Mark Paid
                </button>
              </div>
            )}

            {o.status === "confirmed" && (
              <div className="mt-4 flex flex-wrap gap-2 pt-3 border-t border-white/5">
                <button onClick={() => markShipped(o)} className="btn-orange text-xs ml-auto" data-testid={`admin-order-mark-shipped-${o.order_number}`}>
                  <Truck size={14} /> Mark Shipped
                </button>
              </div>
            )}

            {o.status === "shipped" && (
              <div className="mt-4 flex flex-wrap gap-2 pt-3 border-t border-white/5">
                <button onClick={() => markDelivered(o)} className="btn-orange text-xs ml-auto" data-testid={`admin-order-mark-delivered-${o.order_number}`}>
                  <PackageCheck size={14} /> Mark Delivered
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
